import React, { useState } from 'react'
import { Maximize2, X } from 'lucide-react'

interface PreviewCtx {
  licensePlate?: string | null
  driverName?: string | null
  dealId?: number | null
  suggestedRemision?: string | null
  buildingName?: string | null
  driverSignatureUrl?: string | null
}

interface PreviewForm {
  realM3: string
  tripCount: string
  distanceKm: string
  materialType: string
  remisionNumber: string
  photoUrl: string
  residentObservation: string
  loadingZone: string
  unloadingZone: string
}

// RS-1338 v4: material comes from asset_conditions role=MATERIAL (via active-context).
// supplierCompanyName es la única fuente — materialSupplierName fue removido de assets.
interface PreviewMaterial {
  supplierCompanyName?: string | null
  materialType?: string | null
}

interface Props {
  form: PreviewForm
  ctx: PreviewCtx
  materialAssetSelected: PreviewMaterial | null
  currentDate: string
  currentTime: string
  driverSignatureDataUrl?: string | null  // firma recién dibujada (data URL, prioridad sobre ctx.driverSignatureUrl)
}

const Cell: React.FC<{ label: string; value?: React.ReactNode; minH?: string }> = ({
  label, value, minH = '28px',
}) => (
  <>
    <div style={{ fontSize: '8px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
      {label}
    </div>
    <div style={{ fontSize: '11px', fontWeight: 600, minHeight: minH, paddingTop: '2px', wordBreak: 'break-word' }}>
      {value || <span style={{ color: '#bbb' }}>—</span>}
    </div>
  </>
)

const HeaderRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <tr>
    <td colSpan={6} style={{
      background: '#aebbe8', fontWeight: 'bold', textAlign: 'center',
      padding: '4px', border: '1px solid #7d7d7d', fontSize: '11px',
    }}>{children}</td>
  </tr>
)

interface RemisionTableProps {
  form: PreviewForm
  ctx: PreviewCtx
  materialAssetSelected: PreviewMaterial | null
  currentDate: string
  currentTime: string
  driverSignatureDataUrl?: string | null
  fullSize?: boolean
}

const RemisionTable: React.FC<RemisionTableProps> = ({
  form, ctx, materialAssetSelected, currentDate, currentTime, driverSignatureDataUrl, fullSize,
}) => {
  const transportSignature = driverSignatureDataUrl || ctx.driverSignatureUrl || null
  const supplierName = materialAssetSelected?.supplierCompanyName || ''
  const tipoMaterial = form.materialType || materialAssetSelected?.materialType || ''
  const numeroRemision = form.remisionNumber || ctx.suggestedRemision || ''
  const tdStyle: React.CSSProperties = { border: '1px solid #7d7d7d', padding: '5px', verticalAlign: 'top' }

  return (
    <table style={{
      width: '100%',
      borderCollapse: 'collapse',
      fontFamily: 'Arial, Helvetica, sans-serif',
      background: '#fff',
      tableLayout: 'fixed',
      fontSize: fullSize ? '11px' : '9px',
    }}>
      <colgroup>
        <col style={{ width: '16.66%' }} />
        <col style={{ width: '16.66%' }} />
        <col style={{ width: '16.66%' }} />
        <col style={{ width: '16.66%' }} />
        <col style={{ width: '16.66%' }} />
        <col style={{ width: '16.66%' }} />
      </colgroup>
      <tbody>
        <tr>
          <td colSpan={4} style={{ ...tdStyle, padding: '6px', verticalAlign: 'middle' }}>
            <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#1e3a8a' }}>RETRI</div>
            <div style={{ fontSize: '8px', color: '#666' }}>NIT 901.617.482 · GERENCIA@RETRI.COM.CO</div>
          </td>
          <td colSpan={2} style={{ ...tdStyle, textAlign: 'center', verticalAlign: 'middle' }}>
            <div style={{ fontSize: '9px', color: '#666' }}>REMISIÓN</div>
            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#c00' }}>{numeroRemision || '—'}</div>
          </td>
        </tr>

        <tr>
          <td colSpan={3} style={tdStyle}>
            <Cell label="Ciudad y Fecha" value={`Bogotá · ${currentDate}`} />
          </td>
          <td colSpan={3} style={tdStyle}>
            <Cell label="Obra" value={ctx.buildingName || (ctx.dealId ? `Deal #${ctx.dealId}` : '')} />
          </td>
        </tr>

        <tr>
          <td colSpan={2} style={tdStyle}>
            <Cell label="Nombre del Autorizado" value={ctx.driverName || ''} />
          </td>
          <td colSpan={3} style={tdStyle}>
            <Cell label="Vehículo Placa No" value={ctx.licensePlate || ''} />
          </td>
          <td style={tdStyle}>
            <Cell label="Capacidad M³" value={form.realM3} />
          </td>
        </tr>

        <tr>
          <td colSpan={3} style={{ ...tdStyle, width: '50%' }}>
            <Cell label="Sitio de Cargue" value={form.loadingZone} />
          </td>
          <td colSpan={3} style={{ ...tdStyle, width: '50%' }}>
            <Cell label="Sitio de Descargue" value={form.unloadingZone} />
          </td>
        </tr>

        <tr>
          <td colSpan={3} style={tdStyle}>
            <Cell label="Tipo Material Transportado" value={tipoMaterial} />
          </td>
          <td colSpan={3} style={tdStyle}>
            <Cell label="Razón Social (Proveedor)" value={supplierName} />
          </td>
        </tr>

        <tr>
          <td colSpan={3} style={tdStyle}>
            <Cell label="Con Cargo A" value="RETRI" />
          </td>
          <td colSpan={3} style={tdStyle}>
            <Cell label="Hora Registro" value={currentTime} />
          </td>
        </tr>

        <tr>
          <td colSpan={2} style={tdStyle}>
            <Cell label="N° Viajes" value={form.tripCount || '1'} />
          </td>
          <td colSpan={2} style={tdStyle}>
            <Cell label="Distancia (km)" value={form.distanceKm} />
          </td>
          <td colSpan={2} style={tdStyle}>
            <Cell label="m³ reales" value={form.realM3} />
          </td>
        </tr>

        <HeaderRow>FIRMAS</HeaderRow>
        <tr>
          <td colSpan={3} style={{ ...tdStyle, width: '50%', height: '80px', textAlign: 'center', verticalAlign: 'bottom' }}>
            {transportSignature ? (
              <div style={{ height: '50px', marginBottom: '4px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                <img
                  src={transportSignature}
                  alt="Firma transportador"
                  style={{ maxHeight: '50px', maxWidth: '100%', objectFit: 'contain' }}
                />
              </div>
            ) : (
              <div style={{ borderBottom: '1px solid #000', height: '50px', marginBottom: '4px' }} />
            )}
            <div style={{ fontSize: '9px', color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Firma Transportador — {ctx.driverName || '—'}
            </div>
          </td>
          <td colSpan={3} style={{ ...tdStyle, width: '50%', height: '80px', textAlign: 'center', verticalAlign: 'bottom' }}>
            <div style={{ borderBottom: '1px solid #000', height: '50px', marginBottom: '4px' }} />
            <div style={{ fontSize: '9px', color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Recibido Por — Residente
            </div>
          </td>
        </tr>

        {form.photoUrl && (
          <>
            <HeaderRow>FOTO REMISIÓN</HeaderRow>
            <tr>
              <td colSpan={6} style={{ ...tdStyle, textAlign: 'center', padding: '8px' }}>
                <img
                  src={form.photoUrl}
                  alt="Remisión"
                  style={{ maxWidth: fullSize ? '600px' : '320px', maxHeight: fullSize ? '420px' : '240px', objectFit: 'contain' }}
                />
              </td>
            </tr>
          </>
        )}
      </tbody>
    </table>
  )
}

const RemisionVolquetaPreview: React.FC<Props> = (props) => {
  const [open, setOpen] = useState(false)
  const tableProps = {
    form: props.form,
    ctx: props.ctx,
    materialAssetSelected: props.materialAssetSelected,
    currentDate: props.currentDate,
    currentTime: props.currentTime,
    driverSignatureDataUrl: props.driverSignatureDataUrl,
  }

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
          Vista previa de remisión
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-xs text-retri-blue font-medium flex items-center gap-1 hover:underline"
        >
          <Maximize2 className="w-3.5 h-3.5" />
          Ver en grande
        </button>
      </div>
      <div
        className="bg-white shadow-sm rounded border border-gray-200 cursor-pointer overflow-hidden"
        onClick={() => setOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter') setOpen(true) }}
        title="Toca para ver en grande"
      >
        <div className="p-2">
          <RemisionTable {...tableProps} />
        </div>
      </div>
      <p className="mt-1 text-[10px] text-gray-400 italic text-center">
        Toca el recibo para verlo en detalle · se actualiza mientras llenas el form
      </p>

      {/* Modal fullscreen — horizontal-friendly scroll en mobile */}
      {open && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-2"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white rounded-lg max-w-[95vw] max-h-[95vh] overflow-auto p-3 relative shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="sticky top-0 right-0 float-right bg-white rounded-full p-1 shadow border border-gray-200 z-10"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5 text-gray-700" />
            </button>
            <div className="mb-2 text-xs uppercase tracking-wide text-gray-500 font-semibold pr-8">
              Vista previa de remisión
            </div>
            <p className="text-[10px] text-gray-400 mb-3 pr-8">
              Si está cortada, rota el dispositivo o desliza horizontalmente.
            </p>
            <div className="overflow-x-auto" style={{ minWidth: '320px' }}>
              <div style={{ minWidth: '720px' }}>
                <RemisionTable {...tableProps} fullSize />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default RemisionVolquetaPreview
